import { demoFunc } from './dees-form-submit.demo.js';
import {
  customElement,
  html,
  DeesElement,
  css,
  cssManager,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import type { DeesForm } from '../dees-form/dees-form.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-button/dees-button/dees-button.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-form-submit': DeesFormSubmit;
  }
}

@customElement('dees-form-submit')
export class DeesFormSubmit extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Form', 'Button'];

  // =============================================
  // Properties forwarded to internal dees-button
  // =============================================

  @property({ type: Boolean, reflect: true })
  accessor disabled = false;

  @property({ type: String })
  accessor text!: string;

  @property({ type: String })
  accessor status: 'normal' | 'pending' | 'success' | 'error' = 'normal';

  @property({ type: String, reflect: true })
  accessor type: 'default' | 'accent' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link' | 'normal' | 'highlighted' | 'discreet' | 'big' = 'default';

  @property({ type: String, reflect: true })
  accessor size: 'sm' | 'default' | 'lg' | 'icon' | 'small' | 'normal' | 'large' = 'default';

  @property({ type: String })
  accessor icon!: string;

  @property({ type: String })
  accessor iconPosition: 'left' | 'right' = 'left';

  /** stretches the button face to the full host width — see dees-button.fullWidth */
  @property({ type: Boolean, reflect: true, attribute: 'full-width' })
  accessor fullWidth: boolean = false;

  constructor() {
    super();
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: inline-block;
      }
      /* The host has to stretch too, or forwarding fullWidth only widens the face inside a
         still shrink-wrapped host and the property looks broken. */
      :host([full-width]) {
        display: block;
        width: 100%;
      }
      dees-button {
        width: 100%;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <dees-button
        .status=${this.status}
        .type=${this.type}
        .size=${this.size}
        .icon=${this.icon}
        .iconPosition=${this.iconPosition}
        .text=${this.text}
        .fullWidth=${this.fullWidth}
        ?disabled=${this.disabled}
        @clicked=${this.submit}
      ></dees-button>
    `;
  }

  public async firstUpdated() {
    // Capture this wrapper's light-DOM text as the controlled label for its nested button.
    if (!this.text) {
      const slotText = this.textContent?.trim();
      if (slotText) {
        this.text = slotText;
      }
    }
  }

  public async submit() {
    if (this.disabled || this.status !== 'normal') {
      return;
    }
    // Walk up the DOM tree to find the nearest dees-form element
    const parentFormElement = this.closest('dees-form') as DeesForm;
    if (parentFormElement && parentFormElement.gatherAndDispatch) {
      await parentFormElement.gatherAndDispatch();
    }
  }

  /** Focus without submitting. Call submit() to explicitly gather and dispatch form data. */
  public focus(optionsArg?: FocusOptions): void {
    this.shadowRoot?.querySelector('dees-button')?.focus(optionsArg);
  }

  public blur(): void {
    this.shadowRoot?.querySelector('dees-button')?.blur();
  }
}
